/**
 * Провайдер Яндекс.Метрики (RU-инстанс, vsenapole.ru).
 *
 * Счётчик один на весь домен — лендинг, app. и тенантские поддомены, — чтобы
 * воронка «зашёл на сайт → зарегистрировался → создал первую Работу» считалась
 * сквозной. Разделение тенантов не по счётчикам, а по userParams; см.
 * metrika_migration_plan.md, решение 4.2.
 *
 * ⚠️ Главная разница с PostHog, из которой растёт вся конструкция:
 * произвольных событий у Метрики нет. Есть цели (лимит 200 на счётчик) и
 * параметры визитов (иерархия, без лимита). У нас 611 имён событий, поэтому
 * каждый track() уходит двумя слоями:
 *   1) всегда — в параметры визита, чтобы событие вообще существовало
 *      в отчётах и Logs API;
 *   2) дополнительно — reachGoal, если имя есть в реестре целей.
 * Только второй слой даёт конверсии, воронки и — важнее всего — фильтр
 * записей Вебвизора: запись, не помеченную целью, в интерфейсе не найти.
 *
 * Реестр целей появится на Фазе 3 (analytics/registry.js). До него работает
 * первый слой: события копятся в параметрах и не теряются.
 */

import { BASE_DOMAIN } from '../../branding';
import { isGoal } from '../registry';
import { analyticsUrl } from '../url';

const COUNTER_SRC = 'https://mc.yandex.ru/metrika/tag.js';

let ready = false;
let counterId = null;

export const key = 'metrika';

export function isConfigured() {
  return !!process.env.REACT_APP_YM_ID;
}

// Официальный инициализатор Метрики: заводит очередь window.ym до того, как
// приедет tag.js, и подтягивает сам скрипт. Переписан из сниппета в читаемый
// вид — логика та же, включая защиту от повторной вставки тега (лендинг и
// приложение могут оказаться на одной странице при переходах внутри домена).
function bootstrap() {
  if (window.ym) return;

  window.ym = function ym(...args) {
    (window.ym.a = window.ym.a || []).push(args);
  };
  window.ym.l = Number(new Date());

  const already = Array.from(document.scripts).some((s) => s.src === COUNTER_SRC);
  if (already) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = COUNTER_SRC;
  const first = document.getElementsByTagName('script')[0];
  first.parentNode.insertBefore(script, first);
}

function ym(...args) {
  if (!ready) return;
  // Очередь window.ym существует с момента bootstrap(), поэтому вызовы до
  // загрузки tag.js не теряются — они выполнятся, когда скрипт приедет.
  window.ym(counterId, ...args);
}

export function init() {
  if (ready || !isConfigured()) return;

  // Dev-трафик в Метрику не пишем — на PostHog это делал opt_out_capturing()
  // в loaded-колбэке. У Метрики аналога нет, поэтому просто не инициализируем.
  //
  // Исключение — REACT_APP_ANALYTICS_DEV=1: включает провайдер на локальном
  // стенде. Нужен, потому что Вебвизор иначе непроверяем в принципе: главный
  // риск миграции (воспроизводится ли запись закрытой авторизацией SPA)
  // пришлось бы проверять сразу на PROD, уже записав туда реальные суммы и
  // персданные тенантов.
  //
  // ⚠️ Использовать только с ОТДЕЛЬНЫМ dev-счётчиком, у которого в разрешённые
  // адреса добавлен localhost. С продуктовым ID флаг заливает тестовые визиты
  // в боевые данные и кривит воронки с первого дня.
  const devForced = process.env.REACT_APP_ANALYTICS_DEV === '1';
  if (process.env.NODE_ENV === 'development' && !devForced) return;

  counterId = Number(process.env.REACT_APP_YM_ID);
  bootstrap();

  // Набор параметров — ровно тот, что Метрика отдаёт как рекомендованный код
  // для этого счётчика. Отклонения от него стоили нам дня разбирательств:
  // поддержка на любой нестандартный init отвечает «не гарантируем работу
  // функций», и проверить это утверждение можно только став стандартными.
  //
  // ⚠️ url передаётся ЯВНО и уже очищенным. Это ключевой момент: сам по себе
  // автоматический первый хит ушёл бы с сырым window.location.href, а входная
  // точка у нас бывает какой угодно — /q/<токен> (публичная смета),
  // /invite/<токен>, /e/<токен>, ?email= на логине. Один раз мы уже видели
  // полный токен сметы в отчёте «Просмотры URL».
  //
  // Стандартный код Метрики сам предусматривает явный url — поэтому выбирать
  // между «работающий Вебвизор» и «токены не утекают» не приходится:
  // передаём санитизированный адрес и остаёмся при этом стандартными.
  // ⚠️ ssr НЕ передаём, хотя рекомендованный Метрикой код для этого счётчика
  // его содержит. `ssr: true` означает «страница отрисована на сервере»: в
  // этом режиме счётчик НЕ шлёт автоматический хит, ожидая явного после
  // гидратации. У нас CRA и клиентский рендеринг — параметр был бы враньём,
  // и на практике он обнулил пейджвью полностью (ни одного `watch` в сети).
  // В коде счётчика он появился потому, что в его настройках стоит
  // code_options.ssr: true — это чинится на стороне счётчика, не здесь.
  window.ym(counterId, 'init', {
    webvisor: true,
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    referrer: document.referrer,
    url: analyticsUrl(window.location.pathname, window.location.search),
  });

  ready = true;
}

// Разделение тенантов внутри общего счётчика. setUserID склеивает визиты
// одного человека между устройствами, userParams дают срез по организации —
// это замена person-свойствам PostHog и единственный способ отделить одного
// тенанта от другого в отчётах.
export function identify(user) {
  if (!ready) return;

  // Домен берём из branding (BASE_DOMAIN приезжает из env), а не константой:
  // один образ обслуживает fixty.io и vsenapole.ru, и хардкод любого из них
  // разъехался бы на втором инстансе.
  const orgDomain = user.subdomain ? `${user.subdomain}.${BASE_DOMAIN}` : null;

  ym('setUserID', String(user.id));
  ym('userParams', {
    role: user.role,
    is_demo: user.is_demo ?? false,
    organization_id: user.organization_id ?? null,
    org_subdomain: user.subdomain || null,
    org_domain: orgDomain,
  });
}

// У Метрики нет аналога posthog.reset(): идентичность посетителя живёт в
// _ym_uid и по своей природе не сбрасывается. Поэтому смену пользователя в
// одном браузере не «забываем», а помечаем — чтобы такие визиты можно было
// отсечь сегментом, а не гадать, почему в одной сессии две роли.
export function reset() {
  if (!ready) return;
  ym('params', { identity_switched: 1 });
}

// Первый сегмент имени события — домен ('quote_line_added' → 'quote').
// Параметры визитов иерархичны, и без группировки отчёт превращается в
// плоский список из шестисот ключей, по которому невозможно читать.
function domainOf(event) {
  const i = event.indexOf('_');
  return i > 0 ? event.slice(0, i) : event;
}

export function capture(event, properties) {
  if (!ready) return;

  // Слой 1 — всегда. Событие существует в отчётах и Logs API независимо от
  // того, завели под него цель или нет.
  const domain = domainOf(event);
  ym('params', { ev: { [domain]: { [event]: properties || 1 } } });

  // Слой 2 — только для реестра. Цель нужна не ради дубля, а ради того, чего
  // параметр не умеет: конверсии, воронки и фильтр записей Вебвизора.
  //
  // ⚠️ Цель должна быть заведена в интерфейсе счётчика с идентификатором,
  // равным имени события. Не заведена — reachGoal уходит вхолостую: ошибки
  // не будет, но и в отчётах ничего не появится.
  if (isGoal(event)) {
    ym('reachGoal', event, properties || undefined);
  }
}

// Пейджвью в SPA. Без этого Метрика засчитает один хит на всю рабочую сессию:
// приложение меняет роуты без перезагрузки, а автоматический хит бывает только
// на полной загрузке страницы.
export function pageview(url) {
  if (!ready) return;
  ym('hit', url);
}

export const hasNativeAutocapture = false; // делегат по data-атрибутам — Фаза 3
export const hasNativePageviews = false;   // hit на смену роута шлём сами
